'use client';
import React, { useEffect, useRef } from 'react';

export default function WhySection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('[data-animate]');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="why" className="py-24 px-4 md:px-6 bg-paper-warm border-y border-[rgba(13,13,13,0.06)]" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-ink-faint mb-5">Why Homeify</p>

        {/* Stats */}
        <div
          data-animate
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20 pb-16 border-b border-[rgba(13,13,13,0.08)]"
          style={{ opacity: 0, transform: 'translateY(28px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)' }}>
          
          <div className="flex flex-col">
            <span className="font-sans font-extrabold text-ink leading-none mb-2" style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.8rem)', letterSpacing: '-0.04em' }}>8,400+</span>
            <span className="text-sm font-bold text-ink mb-1">Jobs Completed</span>
            <span className="text-xs text-ink-faint">Across all 4 services</span>
          </div>
          <div className="flex flex-col">
            <span className="font-sans font-extrabold text-ink leading-none mb-2" style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.8rem)', letterSpacing: '-0.04em' }}>Quick</span>
            <span className="text-sm font-bold text-ink mb-1">Avg Response Time</span>
            <span className="text-xs text-ink-faint">Measured over last 90 days</span>
          </div>
          <div className="flex flex-col">
            <span className="font-sans font-extrabold text-ink leading-none mb-2" style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.8rem)', letterSpacing: '-0.04em' }}>7 yrs</span>
            <span className="text-sm font-bold text-ink mb-1">In Business</span>
            <span className="text-xs text-ink-faint">Serving homeowners globally since 2017</span>
          </div>
          <div className="flex flex-col">
            <span className="font-sans font-extrabold text-ink leading-none mb-2" style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.8rem)', letterSpacing: '-0.04em' }}>97%</span>
            <span className="text-sm font-bold text-ink mb-1">Satisfaction Rate</span>
            <span className="text-xs text-ink-faint">Based on 3,200+ reviews</span>
          </div>
        </div>

        {/* Testimonials + Image */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div
            data-animate
            className="space-y-10"
            style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)' }}>
            
            <h2
              className="font-sans font-extrabold text-ink leading-none"
              style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', letterSpacing: '-0.04em' }}>
              
              Trusted by Homeowners<br />
              <span className="font-serif italic font-normal text-ink-muted">Across the Globe.</span>
            </h2>

            <div className="testimonial-accent-black pl-7 space-y-3">
              <p className="text-lg font-medium italic leading-relaxed text-ink-soft">
                &ldquo;My AC died on a Friday evening in July. Homeify had a tech at my door within 40 minutes. Fixed, recharged, and cooling again before midnight. Absolutely worth every penny.&rdquo;
              </p>
              <div>
                <p className="font-bold text-ink text-base">Marcus T.</p>
                <p className="text-ink-faint text-xs uppercase tracking-widest">Homeowner, Phoenix AZ</p>
              </div>
            </div>

            <div className="testimonial-accent-gray pl-7 space-y-3">
              <p className="text-lg font-medium italic leading-relaxed text-ink-soft">
                &ldquo;Lost my car keys on a Sunday. Called Homeify&apos;s locksmith line &mdash; they programmed a replacement key in my driveway in under an hour. No dealer, no tow truck. Game changer.&rdquo;
              </p>
              <div>
                <p className="font-bold text-ink text-base">Jennifer K.</p>
                <p className="text-ink-faint text-xs uppercase tracking-widest">Homeowner, Austin TX</p>
              </div>
            </div>
          </div>

          <div
            data-animate
            className="relative"
            style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s' }}>
            
            <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl border border-[rgba(13,13,13,0.06)]">
              <div className="relative" style={{ width: '100%', height: '100%' }}>
                <img
                  alt="Professional home service technician in uniform smiling at doorstep, suburban home background"
                  loading="lazy"
                  className="object-cover"
                  style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, right: 0, bottom: 0, objectFit: 'cover' }}
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_12ac3c371-1763295220735.png" />
                
              </div>
            </div>

            {/* Badge: Licensed & Insured */}
            <div className="absolute -bottom-6 -left-6 glass border border-[rgba(13,13,13,0.08)] rounded-2xl px-6 py-5 shadow-xl hidden md:block">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-ink flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-paper" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold text-ink text-sm">Licensed &amp; Insured</p>
                  <p className="text-ink-faint text-xs">All technicians background-checked</p>
                </div>
              </div>
            </div>

            {/* BBB Badge */}
            <div
              className="absolute -top-5 -right-5 w-28 h-28 bg-ink rounded-full flex flex-col items-center justify-center text-paper text-center p-3 shadow-xl hidden md:flex"
              style={{ transform: 'rotate(-12deg)' }}>
              
              <span className="text-xl font-extrabold leading-none">A+</span>
              <span className="text-[9px] font-bold uppercase tracking-wider mt-1 opacity-70">BBB Rated</span>
            </div>
          </div>
        </div>
      </div>
    </section>);

}